import { XY } from '@goodboydigital/odie';
import { IPoint, Point } from 'pixi.js';

import { Shared } from '../../App';
import { SplineDisplay } from '../../display/SplineDisplay';
import { Random } from '../../utils/Random';
import { Segment, SegmentPoint } from '../../utils/Segment';
import { CullingComponent } from '../components/CullingComponent';
import { Game } from '../Game';
import { BaseEntity } from './BaseEntity';
import { GroundEntity } from './GroundEntity';
import { SpriteEntity } from './SpriteEntity';

export class GroundTopEntity extends BaseEntity
{
    public spline!: SplineDisplay;
    public culling!: CullingComponent;
    public firstPoint!: SegmentPoint;
    public lastPoint!: SegmentPoint;
    public startAngle = 0;
    public endAngle = 0;

    constructor()
    {
        super();
        this.culling = this.addComponent(CullingComponent);
    }

    public getSpline(ground: GroundEntity, points: IPoint[], offset: number): SplineDisplay
    {
        if (ground.params.img === 'ground-conveyer')
        {
            const texture = Shared.assets.getTexture(Shared.game.scenario.groundConveyorLoop);

            return new SplineDisplay(points, texture, {
                thickness: texture.height,
                baseline: 0.13,
                textureScale: 1,
                repeatDistance: 512,
                flipX: ground.conveyer === 'left',
            });
        }
        else if (ground.params.img === 'ground-slime')
        {
            const texture = Shared.assets.getTexture(Shared.game.scenario.groundSlimeLoop);

            return new SplineDisplay(points, texture, {
                thickness: texture.height,
                baseline: 0.13,
                textureScale: 1,
                repeatDistance: 512,
            });
        }

        const textureName = Shared.game.scenario.groundLoop;
        const texture = Shared.assets.getTexture(textureName);

        return new SplineDisplay(points, texture, {
            thickness: texture.height,
            baseline: 0.13,
            textureScale: 1,
            repeatDistance: 512,
            offsetX: offset,
        });
    }

    public build(ground: GroundEntity, offset = 0): void
    {
        const precision = ground.params.precision;
        const colinearAngle = ground.params.colinearAngle;
        const list = Segment.getPointsFromVerts(ground.params.verts, false, precision, colinearAngle);
        const points = list.map((p) => new Point(p.x, p.y));
        const first = points[0];
        const last = points[points.length - 1];

        if (!ground.flying && !ground.inverse)
        {
            points.unshift(new Point(first.x - 4, first.y));
            points.push(new Point(last.x + 4, last.y));
        }

        if (this.spline)
        {
            if (this.spline.parent) this.spline.parent.removeChild(this.spline);
            this.spline.destroy();
        }

        this.spline = this.getSpline(ground, points, offset);
        this.view.addChild(this.spline);
        this.x = ground.params.x;
        this.y = ground.params.y;
        this.z = 0;
        this.firstPoint = { ...list[0] };
        this.lastPoint = { ...list[list.length - 1] };

        const e = list.length - 1;

        this.startAngle = Math.atan2(list[1].y - list[0].y, list[1].x - list[0].x);
        this.endAngle = Math.atan2(list[e].y - list[e - 1].y, list[e].x - list[e - 1].x);

        this.culling.updateBounds();
    }
}

export class GroundTopFactory
{
    public static placeTop(game: Game, ground: GroundEntity): void
    {
        const top = game.pool.get(GroundTopEntity) as GroundTopEntity;
        const offset = game.level.segmentsLength % 512;

        top.build(ground, offset);

        game.level.addEntity(top);

        const tip1 = game.pool.get(SpriteEntity) as SpriteEntity;
        // const a1 = top.firstPoint.a - (Math.PI * 0.5);
        const a1 = top.startAngle;
        const x1 = top.firstPoint.x + ground.params.x;
        const y1 = top.firstPoint.y + ground.params.y;

        tip1.build({
            x: x1, y: y1,
            r: a1,
            z: 0.001,
            sx: 1,
            sy: 1,
            ax: 0.5,
            ay: 0.13,
            texture: Shared.game.scenario.groundJunction,
        });
        game.level.addEntity(tip1);

        const tip2 = game.pool.get(SpriteEntity) as SpriteEntity;
        // const a2 = top.lastPoint.a - (Math.PI * 0.5);
        const a2 = top.endAngle;
        const x2 = top.lastPoint.x + ground.params.x;
        const y2 = top.lastPoint.y + ground.params.y;

        tip2.build({
            x: x2, y: y2,
            r: a2,
            z: 0.001,
            sx: 1,
            sy: 1,
            ax: 0.5,
            ay: 0.13,
            texture: Shared.game.scenario.groundJunction,
        });
        game.level.addEntity(tip2);

        this.placeDecoration(game, ground);
    }

    public static placeDecoration(game: Game, ground: GroundEntity): void
    {
        if (ground.params.img === 'ground')
        {
            const points1 = Segment.getPointsFromVerts(ground.params.verts, false, 4);

            for (let i = 0; i < points1.length; i++)
            {
                const point = points1[i];
                const xy = {
                    x: point.x + ground.params.x,
                    y: point.y + ground.params.y,
                };

                if (i % 2)
                {
                    this.placeFrontGrass(game, xy, point);
                }
                else
                {
                    this.placeFrontFlower(game, xy, point);
                }
            }
        }

        const points2 = Segment.getPointsFromVerts(ground.params.verts, false, 4);

        for (let i = 0; i < points2.length; i++)
        {
            const point = points2[i];
            const xy = {
                x: point.x + ground.params.x,
                y: point.y + ground.params.y,
            };

            if (i % 2)
            {
                this.placeBackGrass(game, xy, point);
            }
            else
            {
                this.placeBackBushes(game, xy, point);
            }
        }
    }

    public static placeFrontGrass(game: Game, pos: XY, point: SegmentPoint): void
    {
        
        var frontGrassOptions = Shared.game.scenario.frontGrass;
        
        if(frontGrassOptions.length == 0) return;
        
        const grass = game.pool.get(SpriteEntity) as SpriteEntity;
        const a = point.a - (Math.PI * 0.5);
        const x = pos.x - (Math.sin(a) * 20);
        const y = pos.y + (Math.cos(a) * 20);

        grass.build({
            x, y,
            z: 0.01,
            sx: 1,
            sy: 1,
            ax: 0.5,
            ay: 0.9,
            r: a,
            texture: Random.pickArray(frontGrassOptions)
        });
        game.level.addEntity(grass);
    }

    public static placeFrontFlower(game: Game, pos: XY, point: SegmentPoint): void
    {
        var frontFlowerOptions = Shared.game.scenario.frontFlowers;
        
        if(frontFlowerOptions.length == 0) return;
        
        const grass = game.pool.get(SpriteEntity) as SpriteEntity;
        const a = point.a - (Math.PI * 0.5);
        const x = pos.x - (Math.sin(a) * 10);
        const y = pos.y + (Math.cos(a) * 10);

        grass.build({
            x, y,
            z: 0.03,
            sx: 1,
            sy: 1,
            ax: 0.5,
            ay: 0.9,
            r: a,
            texture: Random.pickArray(frontFlowerOptions)
        });
        game.level.addEntity(grass);
    }

    public static placeBackGrass(game: Game, pos: XY, point: SegmentPoint): void
    {
        var backGrassOptions = Shared.game.scenario.backGrass;
        
        if(backGrassOptions.length == 0) return;
        
        const deco1 = game.pool.get(SpriteEntity) as SpriteEntity;
        const a = point.a - (Math.PI * 0.5);
        const x = pos.x - (Math.sin(a) * 10);
        const y = pos.y + (Math.cos(a) * 10);
        // const z = -0.18;
        const z = -0.21;
        const scale = Random.range(2.2, 2.5);

        deco1.build({
            x, y, z,
            sx: scale,
            sy: scale,
            ax: 0.5,
            ay: 0.45,
            r: a,
            texture: Random.pickArray(backGrassOptions)
        });

        game.level.addEntity(deco1);
    }

    public static placeBackBushes(game: Game, pos: XY, point: SegmentPoint): void
    {
        var backBushOptions = Shared.game.scenario.backBushes;
        
        if(backBushOptions.length == 0) return;
        
        const deco1 = game.pool.get(SpriteEntity) as SpriteEntity;
        const a = point.a - (Math.PI * 0.5);
        const x = pos.x - (Math.sin(a) * 30);
        const y = pos.y + (Math.cos(a) * 30);
        // const z = -0.25;
        const z = -0.15;
        
        const texture = Random.pickArray(backBushOptions);
        const scale = Random.range(1.3, 1.5);

        deco1.build({
            x, y, z,
            sx: scale,
            sy: scale,
            ax: 0.5,
            ay: 0.4,
            r: a,
            texture,
        });

        game.level.addEntity(deco1);
    }
}
